import { initializeDB } from '../config/db';
import { LIFT_EVALUATION_STORE, EQUIPMENT_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';

/**
 * Lift Evaluation Form (LEF).
 *
 * Черновики живут в IndexedDB; отправленная форма из стора удаляется и дальше
 * существует только в Symfony — как у остальных форм планшета.
 *
 * Два метода намеренно ходят в сеть: допуски людей и разбор идентификации крана.
 * Свежесть сроков важнее офлайна, а без связи оба поля заполняются руками —
 * форма из-за этого не блокируется.
 */
export const liftEvaluationService = {
    async submit(form) {
        const response = await axiosInstance.post('/lift-evaluation/submit', form);
        return response.data;
    },

    /**
     * Допуски оператора и риггера по имени сотрудника.
     * Без связи бэк отвечает 503 с offline:true — возвращаем его как есть,
     * чтобы форма показала подсказку, а не ошибку.
     */
    async fetchCertifications(fullName) {
        const response = await axiosInstance.get('/lift-evaluation/certifications', {
            params: { name: fullName },
        });
        return response.data;
    },

    /**
     * Отправленные формы по джобу — источники для «Copy to next day».
     * С планшета отправленная форма удаляется, поэтому вчерашнюю берём с сервера.
     */
    async fetchRecentByJob(jobCode, limit = 10) {
        const response = await axiosInstance.get('/lift-evaluation/recent', {
            params: { jobCode, limit },
        });
        return {
            items: response.data?.data || [],
            hasMore: !!response.data?.hasMore,
        };
    },

    /** Полное содержимое отправленной формы — то, что копируем. */
    async fetchByUuid(uuid) {
        const response = await axiosInstance.get(`/lift-evaluation/${uuid}`);
        return response.data?.data || null;
    },

    /**
     * Разобрать Manufacturer / Model / Serial из поля Category справочника техники.
     * Результат бэк запоминает, поэтому обновляем и локальную копию оборудования —
     * в следующий раз данные подставятся уже офлайн.
     */
    async resolveEquipmentIdentity(equipmentId) {
        const response = await axiosInstance.post(`/equipment/${equipmentId}/identity/resolve`);
        if (response.data?.data) {
            await this.updateEquipmentInIndexedDB(response.data.data);
        }
        return response.data;
    },

    /**
     * Свежая запись техники с сервера.
     *
     * Справочник приезжает на планшет инкрементально, поэтому на устройстве,
     * скачавшем его до появления даты годовой инспекции, поля в записи нет.
     * Спрашиваем сервер и сразу обновляем локальную копию — в следующий раз
     * подставится офлайн.
     */
    async fetchEquipment(equipmentId) {
        const response = await axiosInstance.get(`/equipment/${equipmentId}`);
        const data = response.data?.data || null;
        if (data) {
            await this.updateEquipmentInIndexedDB(data);
        }
        return data;
    },

    /** Сохранить исправленную человеком идентификацию крана. */
    async saveEquipmentIdentity(equipmentId, identity) {
        const response = await axiosInstance.patch(`/equipment/${equipmentId}/identity`, identity);
        if (response.data?.data) {
            await this.updateEquipmentInIndexedDB(response.data.data);
        }
        return response.data;
    },

    async updateEquipmentInIndexedDB(equipment) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EQUIPMENT_STORE, 'readwrite');
            await tx.objectStore(EQUIPMENT_STORE).put(equipment);
            await tx.done;
        } catch (error) {
            // Локальная копия — удобство, а не источник правды: на сервере уже сохранено.
            console.error('Error updating equipment in IndexedDB:', error);
        }
    },

    async saveToIndexedDB(formData) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(LIFT_EVALUATION_STORE, 'readwrite');
            const store = tx.objectStore(LIFT_EVALUATION_STORE);

            const dataToSave = {
                ...formData,
                updatedAt: new Date().toISOString(),
            };

            if (!dataToSave.createdAt) {
                dataToSave.createdAt = new Date().toISOString();
            }

            await store.put(dataToSave);
            await tx.done;

            return dataToSave;
        } catch (error) {
            console.error('Error saving Lift Evaluation to IndexedDB:', error);
            throw error;
        }
    },

    async getFromIndexedDB(uuid) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(LIFT_EVALUATION_STORE, 'readonly');
            const data = await tx.objectStore(LIFT_EVALUATION_STORE).get(uuid);
            await tx.done;
            return data;
        } catch (error) {
            console.error('Error reading Lift Evaluation from IndexedDB:', error);
            throw error;
        }
    },

    async getAllFromIndexedDB() {
        try {
            const db = await initializeDB();
            const tx = db.transaction(LIFT_EVALUATION_STORE, 'readonly');
            const data = await tx.objectStore(LIFT_EVALUATION_STORE).getAll();
            await tx.done;
            return data || [];
        } catch (error) {
            console.error('Error reading Lift Evaluation list from IndexedDB:', error);
            return [];
        }
    },

    async deleteFromIndexedDB(uuid) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(LIFT_EVALUATION_STORE, 'readwrite');
            await tx.objectStore(LIFT_EVALUATION_STORE).delete(uuid);
            await tx.done;
        } catch (error) {
            console.error('Error deleting Lift Evaluation from IndexedDB:', error);
            throw error;
        }
    },
};

export default liftEvaluationService;
